import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserCircle, Settings, LogOut, Award, CreditCard, Users, Wallet, Bell, CheckCircle } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useNotifications } from '../contexts/NotificationContext';
import { TAGLEVELS, PLAN_LABELS, DIVISION_VALUES, UNIT_VALUES, INTL_LEVEL_VALUES, isDivisionLevel, isUnitLevel, isIntlLevel } from '../utils/Constants';
import { isEmpty } from '../utils/BasicUtilities';
import useLocalStorageLevel from '../contexts/levelHook';
import { useApiCaller } from '../utils/ApiUtilities';
import { getLogoutUrl, getMeUrl, getRewardsSummaryUrl } from '../utils/UrlUtilities';
import './components.css';

// The two exam families for the split "Select Level" navbar controls. Int'l covers the
// language/standardized tests; every other level is BD. Selection is still a single
// `currentLevel`, so picking from one family automatically deselects the other.
const INTL_TAGLEVELS = TAGLEVELS.filter((i) => INTL_LEVEL_VALUES.includes(i.value));
const BD_TAGLEVELS = TAGLEVELS.filter((i) => !INTL_LEVEL_VALUES.includes(i.value));

export default function NavBar({ title }) {
  const { userState, logoutState } = useUser();
  const { unreadCount } = useNotifications();
  const unreadBadge = unreadCount > 9 ? '9+' : String(unreadCount);
  const navigate = useNavigate();
  const location = useLocation();
  const CallApi = useApiCaller();

  const [showIntlDropdown, setShowIntlDropdown] = useState(false);
  const [showBdDropdown, setShowBdDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSuperAdminDropdown, setShowSuperAdminDropdown] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // mobile collapse (React-controlled)
  const [currentLevel, setCurrentLevel, levelExtra] = useLocalStorageLevel();
  const { division: currentDivision, unit: currentUnit, setSelection } = levelExtra;
  const [flyoutFor, setFlyoutFor] = useState(null); // which level row's Division/Unit flyout is open
  const intlDropdownRef = useRef(null);
  const bdDropdownRef = useRef(null);
  const profileRef = useRef(null);
  const superAdminRef = useRef(null);
  const adminRef = useRef(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [emailVerified, setEmailVerified] = useState(null);
  const [stars, setStars] = useState(0);

  // Pull verification status for the profile dropdown badge.
  useEffect(() => {
    if (!userState?.isLoggedIn) { setEmailVerified(null); return undefined; }
    let cancelled = false;
    (async () => {
      const res = await CallApi({ url: getMeUrl(), method: 'GET', token: true });
      if (!cancelled && res.success) setEmailVerified(Boolean(res.data?.email_verified));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState?.isLoggedIn]);

  // Pull the live star total for the profile header. Refreshed on navigation so newly
  // earned stars (e.g. after a quiz) show up without a re-login.
  useEffect(() => {
    if (!userState?.isLoggedIn) { setStars(0); return undefined; }
    let cancelled = false;
    (async () => {
      const res = await CallApi({ url: getRewardsSummaryUrl(), method: 'GET', token: true });
      if (!cancelled && res.success) setStars(Number(res.data?.total_stars) || 0);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState?.isLoggedIn, location.pathname]);

  const handleLogout = async () => {
    try {
      await CallApi({ url: getLogoutUrl(), method: 'POST', token: true });
    } catch (_) {
      // best-effort: even if the server call fails, still clear client state
    }
    logoutState();
    setShowProfileDropdown(false);
    setNavOpen(false);
    navigate('/login');
  };

  useEffect(() => {
    setActiveMenu(location.pathname);
    // Close the mobile menu after any navigation so the page content shows.
    setNavOpen(false);
    setShowProfileDropdown(false);
    setShowSuperAdminDropdown(false);
    setShowAdminDropdown(false);
  }, [location]);

  const closeLevelMenu = () => {
    setShowIntlDropdown(false);
    setShowBdDropdown(false);
    setFlyoutFor(null);
  };
  // After a level pick, drop the user into the Full Published Model Tests of the model-test store.
  const goToPublishedStore = () => {
    setActiveMenu('/userquiz');
    setNavOpen(false);
    navigate('/userquiz?view=PUBLISHED_QUIZ');
  };
  // Plain level pick (clears any division/unit).
  const handleSelect = (levelValue) => {
    setCurrentLevel(levelValue);
    closeLevelMenu();
    goToPublishedStore();
  };
  // SSC/HSC: level + division.
  const handleSelectDivision = (levelValue, division) => {
    setSelection(levelValue, { division });
    closeLevelMenu();
    goToPublishedStore();
  };
  // VARSITY-*: level + unit.
  const handleSelectUnit = (levelValue, unit) => {
    setSelection(levelValue, { unit });
    closeLevelMenu();
    goToPublishedStore();
  };

  const handleMenuClick = (path) => {
    setActiveMenu(path);
    setNavOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (intlDropdownRef.current && !intlDropdownRef.current.contains(event.target)) {
        setShowIntlDropdown(false);
      }
      if (bdDropdownRef.current && !bdDropdownRef.current.contains(event.target)) {
        setShowBdDropdown(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (superAdminRef.current && !superAdminRef.current.contains(event.target)) {
        setShowSuperAdminDropdown(false);
      }
      if (adminRef.current && !adminRef.current.contains(event.target)) {
        setShowAdminDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getHoverStyle = (itemId) => {
    return hoveredItem === itemId ? {
      backgroundColor: '#ddd6fe',
      color: '#5b21b6',
      transition: 'all 0.2s'
    } : {};
  };

  const getActiveDropdownStyle = (levelValue) => {
    return currentLevel === levelValue ? {
      backgroundColor: '#c4b5fd',
      color: '#4c1d95',
      fontWeight: 'bold'
    } : {};
  };

  const navClass = (path) => `nav-pill${activeMenu === path ? ' active' : ''}`;

  // Shared body for the two "Select Level" dropdowns. `items` is the TAGLEVELS subset
  // for that exam family; markup/behaviour (Division/Unit flyouts, scrolling) is identical
  // to the previous single dropdown — only the level list differs.
  const renderLevelMenu = (items) => (
    <div
      style={{
        position: 'absolute', top: '2.9rem', left: 0,
        display: 'flex', alignItems: 'flex-start', gap: '0.4rem', zIndex: 9999
      }}
      onMouseLeave={() => setFlyoutFor(null)}
      // Any click within the dropdown that isn't scrolling closes it — a level or
      // Division/Unit option first runs its own handler (which selects), then this
      // closes; clicks on captions/empty areas just close. (Scrollbar clicks don't
      // fire element click events, so scrolling the list stays usable.)
      onClick={() => { closeLevelMenu(); }}
    >
      <div style={{ position: 'relative' }}>
        <ul className="kp-scroll-menu" style={{
          margin: 0, backgroundColor: '#ffffff', border: '1px solid #ddd6fe',
          borderRadius: '0.65rem', padding: '0.5rem', minWidth: 'min(230px, 88vw)',
          maxWidth: '88vw', maxHeight: '50vh', overflowY: 'scroll', overflowX: 'hidden',
          listStyle: 'none', boxShadow: '0 18px 44px rgba(76,29,149,0.28)'
        }}>
          {items.map(item => {
            // Parent group (VARSITY / ENGINEERING): a caption + its sub-levels.
            if (item.submenu) {
              return (
                <React.Fragment key={item.value}>
                  <li style={{ padding: '0.35rem 0.5rem 0.15rem', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.4px', color: '#9ca3af', textTransform: 'uppercase' }}>
                    {item.label}
                  </li>
                  {item.submenu.map(sub => {
                    const withUnit = isUnitLevel(sub.value); // VARSITY-* → Unit panel
                    const openHere = withUnit && flyoutFor && flyoutFor.kind === 'Unit' && flyoutFor.level === sub.value;
                    return (
                      <li key={sub.value}>
                        <button
                          style={{
                            ...pillItemStyle,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            ...((hoveredItem === `sub-${sub.value}` || openHere) ? { backgroundColor: '#ddd6fe', color: '#5b21b6' } : {}),
                            ...getActiveDropdownStyle(sub.value)
                          }}
                          onClick={(e) => {
                            // VARSITY-* is not directly selectable — clicking only
                            // opens its Unit panel (and must not close the dropdown).
                            if (withUnit) { e.stopPropagation(); setFlyoutFor({ kind: 'Unit', level: sub.value }); }
                            else { handleSelect(sub.value); }
                          }}
                          onMouseEnter={() => { setHoveredItem(`sub-${sub.value}`); setFlyoutFor(withUnit ? { kind: 'Unit', level: sub.value } : null); }}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          <span>{sub.label}</span>{withUnit ? <span aria-hidden>▸</span> : null}
                        </button>
                      </li>
                    );
                  })}
                </React.Fragment>
              );
            }
            // SSC/HSC → Division panel; other plain levels → no panel.
            const isDiv = isDivisionLevel(item.value);
            const openHere = isDiv && flyoutFor && flyoutFor.kind === 'Division' && flyoutFor.level === item.value;
            return (
              <li key={item.value}>
                <button
                  style={{
                    ...pillItemStyle,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    ...((hoveredItem === `main-${item.value}` || openHere) ? { backgroundColor: '#ddd6fe', color: '#5b21b6' } : {}),
                    ...getActiveDropdownStyle(item.value)
                  }}
                  onClick={(e) => {
                    // SSC/HSC are not directly selectable — clicking only opens
                    // their Division panel (and must not close the dropdown).
                    if (isDiv) { e.stopPropagation(); setFlyoutFor({ kind: 'Division', level: item.value }); }
                    else { handleSelect(item.value); }
                  }}
                  onMouseEnter={() => { setHoveredItem(`main-${item.value}`); setFlyoutFor(isDiv ? { kind: 'Division', level: item.value } : null); }}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <span>{item.label}</span>{isDiv ? <span aria-hidden>▸</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="kp-scroll-fade" aria-hidden>⌄</div>
      </div>

      {flyoutFor && (
        <div style={{
          backgroundColor: '#ffffff', border: '1px solid #ddd6fe', borderRadius: '0.65rem',
          padding: '0.5rem', minWidth: '170px', maxHeight: '50vh', overflowY: 'auto',
          boxShadow: '0 18px 44px rgba(76,29,149,0.28)'
        }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.5px', color: '#7c3aed', textTransform: 'uppercase', padding: '0.1rem 0.5rem 0.45rem' }}>
            {flyoutFor.kind} · {flyoutFor.level}
          </div>
          {(flyoutFor.kind === 'Division' ? DIVISION_VALUES : UNIT_VALUES).map(opt => {
            const key = `${flyoutFor.kind}-${flyoutFor.level}-${opt}`;
            const currentVal = flyoutFor.kind === 'Division' ? currentDivision : currentUnit;
            const active = currentLevel === flyoutFor.level && currentVal === opt;
            return (
              <button
                key={opt}
                style={{
                  ...pillItemStyle,
                  ...(hoveredItem === key ? { backgroundColor: '#ddd6fe', color: '#5b21b6' } : {}),
                  ...(active ? { backgroundColor: '#8b5cf6', color: '#ffffff' } : {})
                }}
                onMouseEnter={() => setHoveredItem(key)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => flyoutFor.kind === 'Division'
                  ? handleSelectDivision(flyoutFor.level, opt)
                  : handleSelectUnit(flyoutFor.level, opt)}
              >
                {active ? '✓ ' : ''}{opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // The selected level's suffix, shown on whichever family button owns the current level.
  const levelSuffix = `${currentLevel}${currentDivision ? ' · Division: ' + currentDivision : (currentUnit ? ' · Unit: ' + currentUnit : '')}`;
  const intlSelected = !isEmpty(currentLevel) && isIntlLevel(currentLevel);
  const bdSelected = !isEmpty(currentLevel) && !isIntlLevel(currentLevel);
  // No level chosen yet → make both level buttons shine to draw the eye.
  const noLevel = isEmpty(currentLevel);
  const attention = noLevel ? ' nav-pill-attention' : '';

  // Profile dropdown menu entries. The first three are functional; the rest are
  // placeholders for features to be built later (still navigable links).
  const profileLinks = [
    { to: '/profile', label: 'Profile & Password Settings', icon: Settings },
    { to: '/rewards', label: 'Rewards Earned', icon: Award },
    { to: '/subscription', label: 'Manage Subscription', icon: CreditCard },
    { to: '/refer', label: 'Refer a Friend', icon: Users },
    { to: '/payments', label: 'Payment Management', icon: Wallet },
    { to: '/notifications', label: 'My Notifications', icon: Bell },
  ];

  return (
    <nav className="navbar navbar-expand-lg" style={{
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      padding: '0.7rem',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <div className="container-fluid">
        <Link className="navbar-brand" to="/">
          <img src="/kobraprep-logo.png" alt="Kobra Prep" style={{ height: '52px', width: 'auto' }} />
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setNavOpen((o) => !o)}
          aria-controls="navbarNav"
          aria-expanded={navOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse${navOpen ? ' show' : ''}`} id="navbarNav">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0" style={{ gap: '0.4rem', alignItems: 'center' }}>
            {userState.isLoggedIn && (
              <>
                <li className="nav-item">
                  <Link to="/home" className={navClass('/home')} onClick={() => handleMenuClick('/home')}>Home</Link>
                </li>

                <li className="nav-item" style={{ position: 'relative' }} ref={intlDropdownRef}>
                  <button
                    onClick={() => { setShowIntlDropdown((v) => !v); setShowBdDropdown(false); setFlyoutFor(null); }}
                    className={`nav-pill${showIntlDropdown ? ' active' : ''}${attention}`}
                  >
                    {intlSelected ? `Select Level (Int'l exams) ${levelSuffix} ▾` : `Select Level (Int'l exams) ▾`}
                  </button>
                  {showIntlDropdown && renderLevelMenu(INTL_TAGLEVELS)}
                </li>

                <li className="nav-item" style={{ position: 'relative' }} ref={bdDropdownRef}>
                  <button
                    onClick={() => { setShowBdDropdown((v) => !v); setShowIntlDropdown(false); setFlyoutFor(null); }}
                    className={`nav-pill${showBdDropdown ? ' active' : ''}${attention}`}
                  >
                    {bdSelected ? `Select Level (BD exams) ${levelSuffix} ▾` : `Select Level (BD exams) ▾`}
                  </button>
                  {showBdDropdown && renderLevelMenu(BD_TAGLEVELS)}
                </li>
              </>
            )}

            {userState.isAdmin && userState.isLoggedIn && (
              <li className="nav-item" style={{ position: 'relative' }} ref={adminRef}>
                <button
                  onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                  className={`nav-pill${showAdminDropdown ? ' active' : ''}`}
                >
                  Admin Feature ▾
                </button>

                {showAdminDropdown && (
                  <ul className="kp-nav-dropdown" style={{
                    position: 'absolute',
                    top: '2.8rem',
                    backgroundColor: '#ede9fe',
                    borderRadius: '0.5rem',
                    padding: '0.5rem',
                    minWidth: 'min(220px, 90vw)',
                    maxWidth: '90vw',
                    zIndex: 9999,
                    listStyle: 'none'
                  }}>
                    {[
                      { to: '/tag', label: 'Tag Management' },
                      { to: '/question', label: 'Question Management' },
                      { to: '/passages', label: 'Passage Management' },
                      { to: '/quiz', label: 'Test Management' },
                      { to: '/auto-quiz-build', label: 'Auto Test Build' },
                      { to: '/notices', label: 'Notice Management' },
                      { to: '/reported', label: 'Reported Management' },
                      { to: '/comment', label: 'Comment Management' },
                      { to: '/admin/ingest', label: 'Ingest Management' },
                      { to: '/admin/vocabulary', label: 'Vocabulary Management' },
                      { to: '/revisions', label: 'Revision Management' },
                    ].map(item => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          style={{
                            ...pillItemStyle,
                            display: 'block',
                            textDecoration: 'none',
                            ...getHoverStyle(`admin-${item.to}`),
                            ...(activeMenu === item.to ? { backgroundColor: '#c4b5fd', color: '#4c1d95' } : {})
                          }}
                          onMouseEnter={() => setHoveredItem(`admin-${item.to}`)}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={() => { setShowAdminDropdown(false); handleMenuClick(item.to); }}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
            {userState.isSuperAdmin && userState.isLoggedIn && (
              <li className="nav-item" style={{ position: 'relative' }} ref={superAdminRef}>
                <button
                  onClick={() => setShowSuperAdminDropdown(!showSuperAdminDropdown)}
                  className={`nav-pill${showSuperAdminDropdown ? ' active' : ''}`}
                >
                  Super Admin Feature ▾
                </button>

                {showSuperAdminDropdown && (
                  <ul className="kp-nav-dropdown" style={{
                    position: 'absolute',
                    top: '2.8rem',
                    backgroundColor: '#ede9fe',
                    borderRadius: '0.5rem',
                    padding: '0.5rem',
                    minWidth: 'min(220px, 90vw)',
                    maxWidth: '90vw',
                    zIndex: 9999,
                    listStyle: 'none'
                  }}>
                    {[
                      { to: '/super-admin', label: 'Admin Management' },
                      { to: '/promo-code-management', label: 'Promo Code Management' },
                      { to: '/award-management', label: 'Award Management' },
                      { to: '/user-management', label: 'User Management' },
                      { to: '/referral-dashboard', label: 'Referral Dashboard' },
                      { to: '/payments-dashboard', label: 'Payments Dashboard' },
                      { to: '/rewards-management', label: 'Rewards Management' },
                    ].map(item => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          style={{
                            ...pillItemStyle,
                            display: 'block',
                            textDecoration: 'none',
                            ...getHoverStyle(`sa-${item.to}`),
                            ...(activeMenu === item.to ? { backgroundColor: '#c4b5fd', color: '#4c1d95' } : {})
                          }}
                          onMouseEnter={() => setHoveredItem(`sa-${item.to}`)}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={() => { setShowSuperAdminDropdown(false); handleMenuClick(item.to); }}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
            {userState.isLoggedIn && (
              <>
                <li className="nav-item"><Link to="/about" className={navClass('/about')} onClick={() => handleMenuClick('/about')}>About</Link></li>
                <li><Link to="/userquiz" className={navClass('/userquiz')} onClick={() => handleMenuClick('/userquiz')}>Model Test Store</Link></li>
                <li><Link to="/vocabulary" className={navClass('/vocabulary')} onClick={() => handleMenuClick('/vocabulary')}>Vocabulary Store</Link></li>
                <li><Link to="/favorite" className={navClass('/favorite')} onClick={() => handleMenuClick('/favorite')}>Starred</Link></li>
                <li><Link to="/stat" className={navClass('/stat')} onClick={() => handleMenuClick('/stat')}>My Stats</Link></li>
                <li><Link to="/contact" className={navClass('/contact')} onClick={() => handleMenuClick('/contact')}>Contact</Link></li>
              </>
            )}
          </ul>

          <div className="d-flex">
            {userState?.isLoggedIn ? (
              <div style={{ position: 'relative' }} ref={profileRef}>
                <button
                  className="nav-pill"
                  onClick={() => setShowProfileDropdown(s => !s)}
                  style={{ borderRadius: '9999px' }}
                >
                  <span style={{ position: 'relative', display: 'inline-flex' }}>
                    <UserCircle size={22} />
                    {unreadCount > 0 && (
                      <span
                        className="badge bg-danger rounded-pill"
                        style={{ position: 'absolute', top: -7, right: -9, fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}
                      >
                        {unreadBadge}
                      </span>
                    )}
                  </span>
                  <span>{userState.name || 'Account'}</span>
                </button>

                {showProfileDropdown && (
                  <div className="kp-nav-dropdown" style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 0.5rem)',
                    backgroundColor: '#ffffff',
                    borderRadius: '0.75rem',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.25)',
                    minWidth: 'min(260px, 90vw)',
                    maxWidth: '90vw',
                    zIndex: 9999,
                    overflow: 'hidden',
                    border: '1px solid #ede9fe'
                  }}>
                    <div style={{ padding: '1rem', background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem' }}>{userState.name || 'User'}</div>
                      {userState.email && (
                        <div style={{ fontSize: '0.8rem', opacity: 0.95, marginBottom: '0.15rem' }}>
                          {userState.email}
                          {emailVerified === true && (
                            <span style={{ color: '#4ade80', marginLeft: '0.35rem', display: 'inline-flex', alignItems: 'center' }}>
                              <CheckCircle size={13} style={{ marginRight: 2 }} /> (Verified)
                            </span>
                          )}
                          {emailVerified === false && (
                            <span style={{ color: '#fde68a', marginLeft: '0.35rem' }}>
                              (Verify from Profile Settings)
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>★ {stars} {stars === 1 ? 'star' : 'stars'}</div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>{PLAN_LABELS[userState.currentPlan] || 'Free'}</div>
                    </div>
                    <div style={{ padding: '0.35rem 0' }}>
                      {profileLinks.map(({ to, label, icon: Icon }) => (
                        <Link
                          key={to}
                          to={to}
                          className="profile-menu-item"
                          onClick={() => setShowProfileDropdown(false)}
                        >
                          <Icon size={16} /> {label}
                          {to === '/notifications' && unreadCount > 0 && (
                            <span className="badge bg-danger rounded-pill" style={{ marginLeft: '0.5rem' }}>
                              {unreadBadge}
                            </span>
                          )}
                        </Link>
                      ))}
                      <div style={{ borderTop: '1px solid #f1f5f9', margin: '0.35rem 0' }} />
                      <button className="profile-menu-item" onClick={handleLogout} style={{ color: '#dc2626' }}>
                        <LogOut size={16} /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link className="btn kp-btn-signup me-2" to="/signup" onClick={() => handleMenuClick('/signup')}>Sign Up</Link>
                <Link className="btn kp-btn-login" to="/login" onClick={() => handleMenuClick('/login')}>Login</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

// Styles for the level dropdown items (light theme)
const pillItemStyle = {
  backgroundColor: '#ede9fe',
  borderRadius: '9999px',
  padding: '0.3rem 0.8rem',
  fontWeight: 'bold',
  color: '#4c1d95',
  marginBottom: '0.25rem',
  border: 'none',
  textAlign: 'left',
  width: '100%',
  whiteSpace: 'nowrap',
  transition: 'all 0.3s',
  cursor: 'pointer'
};

const disabledStyle = {
  color: '#aaa',
  cursor: 'not-allowed',
  backgroundColor: '#f3f4f6'
};
